/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useState} from 'react';
import {showChangeGroup} from '../../store/actions/global-group-chooser';
import {setNotificationsPanelStatus} from '../../store/actions/notifications';

// how long the side nav's collapse animation runs before a drawer that
// waited on it is opened ...
export const SIDE_NAV_COLLAPSE_TRANSITION_MS = 250;

// how long a drawer waits on the side nav to collapse before it is opened
// regardless ...
export const SIDE_NAV_DRAWER_OPEN_TIMEOUT = 15000;

/**
 * Identifies each drawer launched from the current-theme side nav.
 */
export enum SideNavDrawer {
  GroupChooser = 'groupChooser',
  Notifications = 'notifications',
  Profile = 'profile',
  TrackingFilter = 'trackingFilter'
}

/**
 * Defines the inputs required to open a drawer once the side nav collapses.
 */
interface UseCollapseBeforeOpenParams {
  enabled: boolean;
  isOpen: boolean;
  navigationCollapsed: boolean;
  onOpen: () => void;
}

/**
 * Defines the inputs required to manage the current-theme side nav drawers.
 */
interface UseSideNavDrawersParams {
  dispatch: Function;
  isCurrent: boolean;
  isNotificationsOpen: boolean;
  isViewingGlobalGroup: boolean;
  navigationCollapsed: boolean;
  onCloseTrackingFilter: () => void;
  trackingFilterActive: boolean;
}

/**
 * Opens a drawer docked beside the side nav, first collapsing the nav when
 * it is expanded. While enabled and the nav is expanded, the request is
 * held as `pending` - which callers feed to the nav's forced collapse - and
 * the drawer opens once the collapse animation has run; when the nav is
 * already collapsed, or when disabled (legacy theme), it opens at once.
 *
 * ```typescript
 *
 *   import {useCollapseBeforeOpen} from '@/nav/current/useSideNavDrawers';
 *   ...
 *
 *   const {pending, requestOpen} = useCollapseBeforeOpen({
 *     enabled             : isCurrent,
 *     isOpen              : isProfileOpen,
 *     navigationCollapsed : collapsed,
 *     onOpen              : openProfile
 *   });
 *
 * ```
 *
 * @param   params The drawer's open state, the nav's collapsed state, and
 *                 the callback that opens the drawer.
 * @returns The pending flag, and the callbacks that request or cancel the
 *          drawer's opening.
 */
export const useCollapseBeforeOpen = ({
  enabled,
  isOpen,
  navigationCollapsed,
  onOpen
}: UseCollapseBeforeOpenParams) => {
  const [pending, setPending] = useState(false);

  const requestOpen = useCallback(function() {
    if (enabled && !navigationCollapsed) {
      setPending(true);
    } else {
      onOpen();
    }
  }, [enabled, navigationCollapsed, onOpen]);

  const cancelOpen = useCallback(function() {
    setPending(false);
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (pending && navigationCollapsed) {
      timeoutId = setTimeout(onOpen, SIDE_NAV_COLLAPSE_TRANSITION_MS);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [navigationCollapsed, onOpen, pending]);

  useEffect(() => {
    if (pending && isOpen) {
      setPending(false);
    }
  }, [isOpen, pending]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (pending) {
      timeoutId = setTimeout(function() {
        onOpen();
        setPending(false);
      }, SIDE_NAV_DRAWER_OPEN_TIMEOUT);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [onOpen, pending]);

  return {
    cancelOpen,
    pending,
    requestOpen
  };
};

/**
 * Manages the drawers launched from the current-theme side nav - the
 * Tracking filter, Global Group Chooser, Alerts and Profile drawers - so
 * they all behave alike: opening one collapses an expanded side nav first
 * (a collapsed nav is left as is), only one is open at a time, and every
 * one of them can be closed at once when a side nav item is clicked. The
 * legacy theme keeps its own behavior: drawers open at once, and nothing
 * is closed on their behalf.
 *
 * ```typescript
 *
 *   import {useSideNavDrawers} from '@/nav/current/useSideNavDrawers';
 *   ...
 *
 *   const {closeDrawers, forceCollapsed} = useSideNavDrawers({
 *     dispatch,
 *     isCurrent,
 *     isNotificationsOpen,
 *     isViewingGlobalGroup,
 *     navigationCollapsed,
 *     onCloseTrackingFilter : closeTrackingFilterDrawer,
 *     trackingFilterActive  : filterDrawerOpen
 *   });
 *
 * ```
 *
 * @param   params The drawers' open states, the nav's collapsed state, and
 *                 the store dispatcher.
 * @returns The nav's forced collapse flag, the Profile drawer's open state,
 *          and the callbacks that open or close the drawers.
 */
export const useSideNavDrawers = ({
  dispatch,
  isCurrent,
  isNotificationsOpen,
  isViewingGlobalGroup,
  navigationCollapsed,
  onCloseTrackingFilter,
  trackingFilterActive
}: UseSideNavDrawersParams) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const openGroupChooserDrawer = useCallback(function() {
    dispatch(showChangeGroup(true));
  }, [dispatch]);

  const openNotificationsDrawer = useCallback(function() {
    dispatch(setNotificationsPanelStatus(true));
  }, [dispatch]);

  const openProfileDrawer = useCallback(function() {
    setIsProfileOpen(true);
  }, []);

  const groupChooser = useCollapseBeforeOpen({
    enabled : isCurrent,
    isOpen  : isViewingGlobalGroup,
    navigationCollapsed,
    onOpen  : openGroupChooserDrawer
  });

  const notifications = useCollapseBeforeOpen({
    enabled : isCurrent,
    isOpen  : isNotificationsOpen,
    navigationCollapsed,
    onOpen  : openNotificationsDrawer
  });

  const {cancelOpen: cancelGroupChooserOpen}  = groupChooser;
  const {cancelOpen: cancelNotificationsOpen} = notifications;

  // unlike the other drawers, Profile never waits on a nav collapse before
  // opening (see forceCollapsed below), so closing it is a plain state reset ...
  const closeProfile = useCallback(function() {
    setIsProfileOpen(false);
  }, []);

  // closes every open (or opening) drawer but the one given; a no-op in
  // the legacy theme, whose drawers are left to close themselves ...
  const closeDrawers = useCallback(function(keep?: SideNavDrawer) {
    if (isCurrent) {
      if (keep !== SideNavDrawer.TrackingFilter && trackingFilterActive) {
        onCloseTrackingFilter();
      }
      if (keep !== SideNavDrawer.GroupChooser) {
        cancelGroupChooserOpen();
        if (isViewingGlobalGroup) {
          dispatch(showChangeGroup(false));
        }
      }
      if (keep !== SideNavDrawer.Notifications) {
        cancelNotificationsOpen();
        if (isNotificationsOpen) {
          dispatch(setNotificationsPanelStatus(false));
        }
      }
      if (keep !== SideNavDrawer.Profile) {
        closeProfile();
      }
    }
  }, [
    cancelGroupChooserOpen,
    cancelNotificationsOpen,
    closeProfile,
    dispatch,
    isCurrent,
    isNotificationsOpen,
    isViewingGlobalGroup,
    onCloseTrackingFilter,
    trackingFilterActive
  ]);

  const {requestOpen: requestGroupChooserOpen}  = groupChooser;
  const {requestOpen: requestNotificationsOpen} = notifications;

  const openGroupChooser = useCallback(function() {
    closeDrawers(SideNavDrawer.GroupChooser);
    requestGroupChooserOpen();
  }, [closeDrawers, requestGroupChooserOpen]);

  const toggleNotifications = useCallback(function() {
    if (isCurrent && !isNotificationsOpen) {
      closeDrawers(SideNavDrawer.Notifications);
      requestNotificationsOpen();
    } else {
      cancelNotificationsOpen();
      dispatch(setNotificationsPanelStatus(!isNotificationsOpen));
    }
  }, [
    cancelNotificationsOpen,
    closeDrawers,
    dispatch,
    isCurrent,
    isNotificationsOpen,
    requestNotificationsOpen
  ]);

  const openProfile = useCallback(function() {
    closeDrawers(SideNavDrawer.Profile);
    openProfileDrawer();
  }, [closeDrawers, openProfileDrawer]);

  // the side nav is held collapsed while any drawer is open or about to
  // open - the pending flags start the collapse before the drawer itself
  // opens. The open states are current-theme only, since the legacy theme
  // never collapses the nav for them. The Profile drawer is excluded: unlike
  // the others, it docks beside the nav at whatever width it currently is
  // (see useSideNavDockedDrawer), so it never needs the nav force-collapsed
  // to avoid being covered ...
  const forceCollapsed = useMemo(() => (
    trackingFilterActive || groupChooser.pending || notifications.pending ||
    (isCurrent && (isViewingGlobalGroup || isNotificationsOpen))
  ), [
    groupChooser.pending,
    isCurrent,
    isNotificationsOpen,
    isViewingGlobalGroup,
    notifications.pending,
    trackingFilterActive
  ]);

  return {
    closeDrawers,
    closeProfile,
    forceCollapsed,
    isProfileOpen,
    openGroupChooser,
    openProfile,
    toggleNotifications
  };
};

export default useSideNavDrawers;
